import { useEffect, useRef, useState } from "react";

// Interactive brick wall: dark bricks over a green glow that follows the cursor.
export const BrickGrid = ({ className = "" }) => {
    const wrapRef = useRef(null);
    const glowRef = useRef(null);
    const rafRef = useRef(0);
    const [dims, setDims] = useState({ cols: 0, rows: 0 });

    useEffect(() => {
        const compute = () => {
            const el = wrapRef.current;
            if (!el) return;
            const w = el.offsetWidth;
            const h = el.offsetHeight;
            const brickW = w < 640 ? 46 : 64;
            const brickH = w < 640 ? 22 : 28;
            setDims({
                cols: Math.ceil(w / brickW) + 2,
                rows: Math.ceil(h / brickH) + 2,
            });
        };
        compute();
        window.addEventListener("resize", compute);
        return () => window.removeEventListener("resize", compute);
    }, []);

    const onMove = (e) => {
        const el = wrapRef.current;
        const glow = glowRef.current;
        if (!el || !glow) return;
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = 0;
            glow.style.opacity = "1";
            glow.style.transform = `translate(${x - 220}px, ${y - 220}px)`;
        });
    };

    const onLeave = () => {
        if (glowRef.current) glowRef.current.style.opacity = "0";
    };

    return (
        <div
            ref={wrapRef}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            data-testid="brick-grid"
            className={`absolute inset-0 overflow-hidden bg-[#080808] ${className}`}
        >
            {/* Green glow behind the bricks */}
            <div
                ref={glowRef}
                className="pointer-events-none absolute h-[440px] w-[440px] rounded-full opacity-0 transition-opacity duration-300"
                style={{
                    background:
                        "radial-gradient(circle, hsl(119 99% 46% / 0.55) 0%, hsl(119 99% 46% / 0.18) 40%, transparent 70%)",
                    filter: "blur(12px)",
                    left: 0,
                    top: 0,
                }}
            />
            {/* Brick rows */}
            <div className="relative flex flex-col gap-[3px] p-[3px]">
                {Array.from({ length: dims.rows }).map((_, r) => (
                    <div
                        key={r}
                        className="flex gap-[3px]"
                        style={{ marginLeft: r % 2 === 0 ? 0 : "32px" }}
                    >
                        {Array.from({ length: dims.cols }).map((_, c) => (
                            <div
                                key={c}
                                className="brick-cell h-[22px] w-[43px] shrink-0 sm:h-[26px] sm:w-[61px]"
                            />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};
